/**
 * PostNL code management — super/full admin page.
 *
 * Lets an admin filter codes by firm, search by code/surname/initials, edit
 * or delete a code, assign/unassign it to an employee, and bulk-import new
 * codes via a paste-a-CSV modal.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Package, Plus, Search, Upload, Trash2, Edit2, UserPlus, X, Loader2,
  ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';

const UNASSIGNED = '__unassigned__';

export default function PostNLCodes() {
  const { t } = useTranslation();
  const [firms, setFirms] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState('');
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Assignment filter — tri-state: 'all' | 'assigned' | 'unassigned'
  const [assignmentFilter, setAssignmentFilter] = useState('all');

  // Dialogs
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {id?, code, firm_id, surname, initials}
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // code row
  const [assignUserId, setAssignUserId] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFirm, setBulkFirm] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [bulkReplace, setBulkReplace] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Column sort — `key` is one of the column accessors below; `dir` flips
  // between 'asc' and 'desc' when the same column is clicked twice.
  const [sortKey, setSortKey] = useState('code');
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    setSortDir((cur) => (sortKey === key ? (cur === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [fRes, uRes] = await Promise.all([
          api.get('/firms/'),
          api.get('/admin/users?status=ACTIVE'),
        ]);
        const fs = fRes.data?.data || [];
        setFirms(fs);
        // Prefer HYK as the default landing firm (matches the common admin
        // workflow); fall back to the first firm otherwise.
        if (fs.length && !selectedFirm) {
          const hyk = fs.find((f) => (f.name || '').toUpperCase() === 'HYK');
          setSelectedFirm(hyk ? hyk.id : fs[0].id);
        }
        const usersPayload = uRes.data?.data || uRes.data;
        setUsers(Array.isArray(usersPayload) ? usersPayload : (usersPayload?.users || []));
      } catch (e) {
        toast.error('Firmalar/kullanıcılar yüklenemedi');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch codes whenever firm or assignment filter changes. An empty
  // selectedFirm is valid ('Tüm Firmalar') and skips the firm_id query param.
  const fetchCodes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedFirm) params.set('firm_id', selectedFirm);
      if (assignmentFilter === 'unassigned') params.set('unassigned_only', 'true');
      const res = await api.get(`/postnl/codes?${params.toString()}`);
      let list = res.data?.data || [];
      // Backend doesn't support "assigned_only" yet, so filter client-side.
      if (assignmentFilter === 'assigned') {
        list = list.filter((c) => !!c.assigned_user_id);
      }
      setCodes(list);
    } catch {
      toast.error('PostNL kodları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchCodes(); /* eslint-disable-next-line */ }, [selectedFirm, assignmentFilter]);

  // Build a quick firm-id → firm-name map so the table can show the firm
  // column when the "Tüm Firmalar" view is active.
  const firmNameById = useMemo(() => {
    const m = {};
    for (const f of firms) m[f.id] = f.name;
    return m;
  }, [firms]);

  // Client-side search + sort. Sort uses Turkish-aware locale compare so
  // characters like ç/ğ/ı/ş land in the right place when ordering A→Z.
  const filtered = useMemo(() => {
    const base = !search.trim() ? codes : codes.filter((c) =>
      (c.code || '').toLowerCase().includes(search.trim().toLowerCase()) ||
      (c.surname || '').toLowerCase().includes(search.trim().toLowerCase()) ||
      (c.initials || '').toLowerCase().includes(search.trim().toLowerCase()) ||
      (c.assigned_user_name || '').toLowerCase().includes(search.trim().toLowerCase()),
    );
    const accessor = (row) => {
      switch (sortKey) {
        case 'surname':  return row.surname || '';
        case 'initials': return row.initials || '';
        case 'assigned': return row.assigned_user_name || row.assigned_user_email || '';
        case 'code':
        default:         return row.code || '';
      }
    };
    const sorted = [...base].sort((a, b) =>
      accessor(a).localeCompare(accessor(b), 'tr', { sensitivity: 'base', numeric: true })
    );
    return sortDir === 'desc' ? sorted.reverse() : sorted;
  }, [codes, search, sortKey, sortDir]);

  const openCreate = () => {
    setEditing({ code: '', firm_id: selectedFirm, surname: '', initials: '' });
    setEditOpen(true);
  };
  const openEdit = (row) => {
    setEditing({ ...row });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editing?.code?.trim()) {
      toast.error('Kod gerekli');
      return;
    }
    try {
      if (editing.id) {
        await api.put(`/postnl/codes/${editing.id}`, {
          code: editing.code.trim(),
          firm_id: editing.firm_id,
          surname: editing.surname || '',
          initials: editing.initials || '',
        });
      } else {
        await api.post('/postnl/codes', {
          code: editing.code.trim(),
          firm_id: editing.firm_id,
          surname: editing.surname || '',
          initials: editing.initials || '',
        });
      }
      toast.success('Kaydedildi');
      setEditOpen(false);
      setEditing(null);
      fetchCodes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kayıt hatası');
    }
  };

  const openAssign = (row) => {
    setAssignTarget(row);
    setAssignUserId(row.assigned_user_id || UNASSIGNED);
    setAssignOpen(true);
  };
  const saveAssign = async () => {
    try {
      await api.post(`/postnl/codes/${assignTarget.id}/assign`, {
        user_id: assignUserId === UNASSIGNED ? null : assignUserId,
      });
      toast.success('Atama güncellendi');
      setAssignOpen(false);
      setAssignTarget(null);
      fetchCodes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Atama hatası');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/postnl/codes/${deleteId}`);
      toast.success('Silindi');
      setDeleteId(null);
      fetchCodes();
    } catch {
      toast.error('Silinemedi');
    }
  };

  // Parse pasted CSV-ish text:
  //   <code><TAB|comma|whitespace><surname>[<sep><initials>]
  // Supports both "8140014, Ozer, O." and "8199981  Asenov  S.M." styles.
  const parseBulkText = (text) => {
    const items = [];
    for (const raw of text.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line) continue;
      const parts = line.split(/\t|,|\s{2,}|\s+/).map((p) => p.trim()).filter(Boolean);
      if (parts.length < 2) continue;
      const code = parts[0];
      if (!/^\d+$/.test(code)) continue; // skip headers / noise
      const surname = parts[1] || '';
      const initials = parts.slice(2).join(' ');
      items.push({ code, surname, initials });
    }
    return items;
  };

  const runBulkImport = async () => {
    if (!bulkFirm) { toast.error('Firma seç'); return; }
    const parsed = parseBulkText(bulkText);
    if (parsed.length === 0) {
      toast.error('Geçerli satır bulunamadı');
      return;
    }
    setBulkBusy(true);
    try {
      const res = await api.post('/postnl/codes/bulk', {
        items: parsed.map((p) => ({ ...p, firm_id: bulkFirm })),
        replace: bulkReplace,
      });
      const { inserted = 0, skipped = 0 } = res.data?.data || {};
      toast.success(`${inserted} eklendi, ${skipped} atlandı`);
      setBulkOpen(false);
      setBulkText('');
      if (bulkFirm === selectedFirm) fetchCodes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import hatası');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white shadow">
            <Package className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              PostNL Kodları
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Firma başına sürücü kodlarını yönet ve çalışanlara ata.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="postnl-bulk-btn">
                <Upload className="h-4 w-4 mr-2" /> Toplu Import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Toplu PostNL Kodu Import</DialogTitle>
                <DialogDescription>
                  Her satır bir kod. Kod – Soyad – Baş harf sırasıyla, boşluk / sekme / virgül ayraç. Örn:
                  <br /><code className="text-xs">8140014, Ozer, O.</code> veya <code className="text-xs">8199981  Asenov  S.M.</code>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Firma</Label>
                  <Select value={bulkFirm} onValueChange={setBulkFirm}>
                    <SelectTrigger data-testid="postnl-bulk-firm"><SelectValue placeholder="Firma seç" /></SelectTrigger>
                    <SelectContent>
                      {firms.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Veri</Label>
                  <textarea
                    rows={8}
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder={'8140014, Ozer, O.\n8199981  Asenov  S.M.'}
                    className="w-full text-sm font-mono rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    data-testid="postnl-bulk-text"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={bulkReplace}
                    onChange={(e) => setBulkReplace(e.target.checked)}
                    data-testid="postnl-bulk-replace"
                  />
                  Bu firmanın mevcut kodlarını silip yerine yaz (aksi halde çakışanlar atlanır)
                </label>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setBulkOpen(false)}>İptal</Button>
                <Button onClick={runBulkImport} disabled={bulkBusy} data-testid="postnl-bulk-save">
                  {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'İçe Aktar'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700" data-testid="postnl-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Yeni Kod
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="sm:w-56">
              <Select value={selectedFirm || '__all__'} onValueChange={(v) => setSelectedFirm(v === '__all__' ? '' : v)}>
                <SelectTrigger data-testid="postnl-firm-filter"><SelectValue placeholder="Firma" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tüm Firmalar</SelectItem>
                  {firms.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:w-48">
              <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
                <SelectTrigger data-testid="postnl-assignment-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="assigned">Sadece atanmış</SelectItem>
                  <SelectItem value="unassigned">Sadece atanmamış</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                placeholder="Kod veya isim ara…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="postnl-search"
              />
            </div>
          </div>
          <CardTitle className="text-sm font-medium text-slate-500 mt-3">
            {filtered.length} kod
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 flex items-center justify-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">Kayıt yok.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHead label="Kod" k="code" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortableHead label="Soyad" k="surname" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortableHead label="Baş Harf" k="initials" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    {!selectedFirm && <TableHead>Firma</TableHead>}
                    <SortableHead label="Atanan" k="assigned" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <TableHead className="text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.id} data-testid={`postnl-row-${row.id}`}>
                      <TableCell className="font-mono font-semibold">{row.code}</TableCell>
                      <TableCell>{row.surname}</TableCell>
                      <TableCell className="text-slate-500">{row.initials}</TableCell>
                      {!selectedFirm && (
                        <TableCell>
                          <Badge variant="outline" className="text-xs font-normal">
                            {firmNameById[row.firm_id] || row.firm_id?.slice(0, 8) || '—'}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell>
                        {row.assigned_user_id ? (
                          <Badge variant="outline" className="font-normal">
                            {row.assigned_user_name || row.assigned_user_email || '—'}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400">Atanmadı</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openAssign(row)} data-testid={`postnl-assign-${row.id}`} title="Ata">
                            <UserPlus className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(row)} data-testid={`postnl-edit-${row.id}`} title="Düzenle">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setDeleteId(row.id)} data-testid={`postnl-delete-${row.id}`} title="Sil">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit / Create dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Kodu Düzenle' : 'Yeni PostNL Kodu'}</DialogTitle>
            <DialogDescription>Kod, soyad ve baş harf bilgileri.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Firma</Label>
                <Select value={editing.firm_id} onValueChange={(v) => setEditing({ ...editing, firm_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kod</Label>
                <Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} data-testid="postnl-edit-code" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Soyad</Label>
                  <Input value={editing.surname || ''} onChange={(e) => setEditing({ ...editing, surname: e.target.value })} data-testid="postnl-edit-surname" />
                </div>
                <div>
                  <Label>Baş Harf</Label>
                  <Input value={editing.initials || ''} onChange={(e) => setEditing({ ...editing, initials: e.target.value })} placeholder="A.S." data-testid="postnl-edit-initials" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>İptal</Button>
            <Button onClick={saveEdit} data-testid="postnl-edit-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kodu Ata</DialogTitle>
            <DialogDescription>
              {assignTarget?.code} — {assignTarget?.surname} {assignTarget?.initials}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Kullanıcı</Label>
            <Select value={assignUserId} onValueChange={setAssignUserId}>
              <SelectTrigger data-testid="postnl-assign-user"><SelectValue placeholder="Kullanıcı seç" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={UNASSIGNED}>
                  <span className="flex items-center gap-1 text-slate-500"><X className="h-3 w-3" /> Atamayı kaldır</span>
                </SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.full_name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignOpen(false)}>İptal</Button>
            <Button onClick={saveAssign} data-testid="postnl-assign-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kodu sil?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Click-to-sort table head. Inactive columns get a faint up/down icon
 * so the operator can tell they're sortable; the active column shows the
 * direction with a solid arrow. */
function SortableHead({ label, k, sortKey, sortDir, onSort }) {
  const active = sortKey === k;
  const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 select-none hover:text-blue-600 ${active ? 'text-blue-600 font-semibold' : ''}`}
        data-testid={`postnl-sort-${k}`}
      >
        {label}
        <Icon className={`h-3 w-3 ${active ? '' : 'opacity-40'}`} />
      </button>
    </TableHead>
  );
}
